import React, { Component } from 'react'
import PropTypes from 'prop-types'

import {
  ConfirmationModal,
  ConfirmationModalBase,
  ModalAction,
  ModalActionTypes,
} from '@acorns/web-components'
import { DocumentNode } from 'apollo-boost'
import { path } from 'ramda'
import { compose, getContext, lifecycle, withHandlers, withState } from 'recompose'

import AlertBadgePath from 'assets/icons/alert-badge.png'

export interface IErrorDetails {
  actions?: ModalAction[]
  message?: string | React.ReactNode | (() => React.ReactNode)
  eventFields?: object
  title?: string | React.ReactNode | (() => React.ReactNode)
  image?: string
  children?: React.ReactNode
}

export interface IErrorPayload {
  graphQLErrors: any
  networkError: any
}

export interface IErrorModalProps {
  closeErrorModal: () => void
  errorDetails: IErrorDetails
  showErrorModal: (details: IErrorDetails) => void
  showModal: boolean
}

interface ILifecycleProps {
  innerRef: (ref: any) => void
}

type ErrorHandler = () => IErrorDetails | false

const mergeDefaults = (handler, details: IErrorDetails = {}): IErrorDetails => {
  return {
    actions: [
      {
        handler,
        label: 'Try Again',
        type: ModalActionTypes.primary,
      },
    ],
    message: 'It looks like something went wrong.',
    title: 'Oops!',
    image: AlertBadgePath,
    ...details,
  }
}

const enhance = compose<IErrorModalProps, ILifecycleProps>(
  withState(
    'errorDetails',
    'setErrorDetails',
    mergeDefaults(() => ({})),
  ),
  withState('showModal', 'setShowModal', false),
  withHandlers({
    closeErrorModal: ({ setShowModal }) => () => {
      setShowModal(false)
    },
  }),
  withHandlers({
    showErrorModal: ({ closeErrorModal, setErrorDetails, setShowModal }) => errorDetails => {
      setErrorDetails(mergeDefaults(closeErrorModal, errorDetails))
      setShowModal(true)
    },
  }),
  lifecycle<ILifecycleProps, {}>({
    componentDidMount() {
      const { innerRef } = this.props

      if (innerRef) {
        innerRef(this)
      }
    },
  }),
)

// Presentational component used for displaying errors
const ErrorModal = enhance(props => {
  const { children, ...rest } = props.errorDetails

  return children ? (
    <ConfirmationModalBase
      actions={props.errorDetails.actions}
      eventFields={props.errorDetails.eventFields}
      isOpen={props.showModal}
      buttonStyle={props.errorDetails.buttonStyle}
    >
      {children}
    </ConfirmationModalBase>
  ) : (
    <ConfirmationModal
      onRequestClose={props.closeErrorModal}
      actions={props.errorDetails.actions}
      message={props.errorDetails.message}
      eventFields={props.errorDetails.eventFields}
      title={props.errorDetails.title}
      image={props.errorDetails.image}
      isOpen={props.showModal}
      buttonStyle={props.errorDetails.buttonStyle}
    />
  )
})

// Registry for graphql error handlers
export class ApolloErrorHandlingProvider extends Component<{}, any> {
  public static childContextTypes = {
    addCustomHandler: PropTypes.func.isRequired,
    closeErrorModal: PropTypes.func.isRequired,
    getCustomHandler: PropTypes.func.isRequired,
    removeCustomHandler: PropTypes.func.isRequired,
    showErrorModal: PropTypes.func.isRequired,
  }

  private modal: React.Component<IErrorModalProps>

  private customHandlerMap: Map<string, boolean | ErrorHandler>

  constructor(props) {
    super(props)

    this.customHandlerMap = new Map<string, boolean | ErrorHandler>()
  }

  public getChildContext() {
    const {
      addCustomHandler,
      closeErrorModal,
      getCustomHandler,
      removeCustomHandler,
      showErrorModal,
    } = this

    return {
      addCustomHandler,
      closeErrorModal,
      getCustomHandler,
      removeCustomHandler,
      showErrorModal,
    }
  }

  public addCustomHandler = (name, handler) => {
    this.customHandlerMap.set(name, handler)
  }

  public closeErrorModal = () => {
    this.modal.props.closeErrorModal()
  }

  public getCustomHandler = name => {
    return this.customHandlerMap.get(name)
  }

  public modalRef = (modal?: React.Component<IErrorModalProps>) => {
    this.modal = modal
  }

  public removeCustomHandler = name => {
    return this.customHandlerMap.delete(name)
  }

  public showErrorModal = details => {
    this.modal.props.showErrorModal(details)
  }

  public render() {
    return (
      <>
        {this.props.children}

        <ErrorModal innerRef={this.modalRef} />
      </>
    )
  }
}

// HOC for use by apollo-link-error to access registered error handlers
// and display error modal
export const withApolloErrorModal = compose(
  getContext({
    getCustomHandler: PropTypes.func,
    showErrorModal: PropTypes.func,
    closeErrorModal: PropTypes.func,
  }),
)

interface IWithErrorHandlerProps {
  addCustomHandler: (name: string, handler: (error: IErrorPayload) => IErrorDetails) => void
  closeErrorModal: () => void
  removeCustomHandler: (name: string) => void
}

type Handler<T> = (
  props: T,
) => (error: IErrorPayload) => IErrorDetails | false | Promise<IErrorDetails>

type Name = string | ((props: any) => string) | DocumentNode

const resolveName = (name, props) => {
  switch (typeof name) {
    case 'function':
      return name(props)
    case 'object':
      return path(['definitions', 0, 'name', 'value'], name)
    default:
      return name
  }
}

// HOC for registering new error handlers for use by components that implement
// graphql queries and mutations
export const withApolloErrorHandler = (name: Name, handler: Handler<any>) =>
  compose<IWithErrorHandlerProps, {}>(
    getContext({
      addCustomHandler: PropTypes.func,
      closeErrorModal: PropTypes.func,
      removeCustomHandler: PropTypes.func,
    }),
    lifecycle<IWithErrorHandlerProps, {}>({
      componentDidMount() {
        this.props.addCustomHandler(resolveName(name, this.props), handler(this.props))
      },

      componentDidUpdate() {
        this.props.addCustomHandler(resolveName(name, this.props), handler(this.props))
      },

      componentWillUnmount() {
        this.props.removeCustomHandler(resolveName(name, this.props))
      },
    }),
  )
